import React, { useState, useEffect, useRef } from 'react';
import './People.css';
import Availability from './Availability';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';
import ParticipantRegistrations from '../components/ParticipantRegistrations';
import ParticipantSwishSessions from '../components/ParticipantSwishSessions';
import PersonTeamLists from '../components/PersonTeamLists';
import PersonTimeOff from '../components/PersonTimeOff';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

// In tab order (the first one the user can see opens by default).
const TAB_PERMISSION_MAP = {
  'people-management': { pageKey: 'admin', sectionKey: 'person' },
  // Per-role active rosters. Each is its own permission section so an
  // admin can grant edit on (e.g.) Active Coaches without granting it on
  // Active Referees. The data lives on Person.isActive<Role>.
  'active-referees': { pageKey: 'admin', sectionKey: 'active-referees' },
  'active-bench':    { pageKey: 'admin', sectionKey: 'active-bench' },
  'active-coaches':  { pageKey: 'admin', sectionKey: 'active-coaches' },
  'active-court':    { pageKey: 'admin', sectionKey: 'active-court' },
  'participants': { pageKey: 'admin', sectionKey: 'participants' },
  'availability': { pageKey: 'admin', sectionKey: 'availability' },
};

// Symmetric per-role config so the four sub-sections share one render
// path. apiKey matches the backend ROLE_REGISTRY key.
const ACTIVE_ROLE_TABS = [
  { tabKey: 'active-referees', apiKey: 'referee', singular: 'Referee',        plural: 'Referees',        flagField: 'isActiveReferee' },
  { tabKey: 'active-bench',    apiKey: 'bench',   singular: 'Bench Official', plural: 'Bench Officials', flagField: 'isActiveBench'   },
  { tabKey: 'active-coaches',  apiKey: 'coach',   singular: 'Coach',          plural: 'Coaches',         flagField: 'isActiveCoach'   },
  { tabKey: 'active-court',    apiKey: 'court',   singular: 'Court Official', plural: 'Court Officials', flagField: 'isActiveCourt'   },
];

export default function People() {
  const { canView, canEdit } = usePermissions();
  const [activeTab, setActiveTab] = useState('');

  // People Management state
  const [people, setPeople] = useState([]);
  const [personForm, setPersonForm] = useState({ name: '', email: '', phoneNumber: '', accountName: '', bsbNumber: '', accountNumber: '', gender: '', isActiveReferee: false, isActiveBench: false, isActiveCoach: false, isActiveCourt: false });

  // Per-role active rosters: list of currently-active people for each
  // sub-section + the candidates pool for the "Add Active …" modal.
  // Both are { [apiKey]: rows[] } maps so the four sub-sections share
  // state shape.
  const [activePeopleByRole, setActivePeopleByRole] = useState({});
  const [activeAddModal, setActiveAddModal] = useState(null); // { apiKey, singular, plural }
  const [activeCandidates, setActiveCandidates] = useState([]);
  const [activeAddSelected, setActiveAddSelected] = useState(new Set());
  const [activeAddSaving, setActiveAddSaving] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [refereeQualifications, setRefereeQualifications] = useState([]);
  const [benchQualifications, setBenchQualifications] = useState([]);
  const [courtQualifications, setCourtQualifications] = useState([]);
  const [coachQualifications, setCoachQualifications] = useState([]);
  const [selectedPersonQualifications, setSelectedPersonQualifications] = useState({
    referee: [],
    benchOfficial: [],
    courtOfficial: [],
    coach: []
  });
  const [qualToAdd, setQualToAdd] = useState({ category: 'referee', qualificationId: '' });
  const [allTeams, setAllTeams] = useState([]);
  // Add Person: 'choose' (Basketball Connect or manual), 'bc' (searching BC), 'form' (the details form).
  const [addStep, setAddStep] = useState('form');
  const [bcQuery, setBcQuery] = useState('');
  const [bcResults, setBcResults] = useState([]);
  const [bcSearching, setBcSearching] = useState(false);

  // Availability state for person modal
  const [personAvailability, setPersonAvailability] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilitySaving, setAvailabilitySaving] = useState(false);

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const DEFAULT_AVAILABILITY = DAY_NAMES.map((name, i) => ({
    dayOfWeek: i, dayName: name, startTime: '06:00', endTime: '23:00', isAvailable: true
  }));

  // Participants state
  const [participants, setParticipants] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [participantForm, setParticipantForm] = useState({
    firstName: '', lastName: '', ageGroup: '', repPlayer: false,
    mobile: '', email: '', contactPersonName: '', notes: '', programLinks: [],
    externalMemberId: '', gender: '',
    dateOfBirth: '', repExperience: '', repLevel: '', heightCm: '', positions: []
  });
  const [editingParticipantId, setEditingParticipantId] = useState(null);

  // Import state
  // (CSV import removed — BC Integration sync replaces it.)

  // Form visibility state
  const [showPersonForm, setShowPersonForm] = useState(false);
  // Edit Person window tab: person | roles | availability | bank
  const [personTab, setPersonTab] = useState('person');
  const [showParticipantForm, setShowParticipantForm] = useState(false);

  // Search state
  const [peopleSearch, setPeopleSearch] = useState('');
  const [participantSearch, setParticipantSearch] = useState('');

  // Archive toggle state
  const [showArchivedPeople, setShowArchivedPeople] = useState(false);
  const [showArchivedParticipants, setShowArchivedParticipants] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedPersonId, setCopiedPersonId] = useState(null);
  const [emailedPersonId, setEmailedPersonId] = useState(null);
  const [emailingPersonId, setEmailingPersonId] = useState(null);

  // Set default active tab to first visible tab
  // Read the People sub-tab from the URL hash. e.g. "#people/participants"
  // means the Participants tab. Empty -> first visible tab.
  const getSubTabFromHash = () => {
    const h = (typeof window !== 'undefined' && window.location.hash) || '';
    const parts = h.replace(/^#/, '').split('/');
    return parts[0] === 'people' ? (parts[1] || '') : '';
  };

  // Pick a tab when the URL points to one + on hashchange.
  useEffect(() => {
    const apply = () => {
      const wanted = getSubTabFromHash();
      if (wanted && TAB_PERMISSION_MAP[wanted] && canView(TAB_PERMISSION_MAP[wanted].pageKey, TAB_PERMISSION_MAP[wanted].sectionKey)) {
        setActiveTab(wanted);
        return;
      }
      // Fallback to the first visible tab when there's no valid sub-tab.
      if (!activeTab) {
        const firstVisibleTab = Object.keys(TAB_PERMISSION_MAP).find(
          tabKey => canView(TAB_PERMISSION_MAP[tabKey].pageKey, TAB_PERMISSION_MAP[tabKey].sectionKey)
        );
        if (firstVisibleTab) setActiveTab(firstVisibleTab);
      }
    };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Mirror tab changes back into the URL so reload + share preserves the
  // selected tab. Only when on the People page.
  useEffect(() => {
    if (!activeTab) return;
    const want = `#people/${activeTab}`;
    const h = window.location.hash || '';
    if (h.startsWith('#people') && h !== want) {
      window.location.hash = want;
    }
  }, [activeTab]);

  // Load data on mount and when archive toggles change
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchivedPeople, showArchivedParticipants]);

  // Auto-dismiss the error toast after 8 seconds. Without this an error
  // (e.g. the backend's "no matching qualification" 400 when ticking
  // Active without a qual) sticks on the screen forever — there's no
  // close button on .error-message and many of the setError call-sites
  // don't have a per-call setTimeout. 8s is long enough to read a 1–2
  // line message; quicker dismissal is via the click handler on the
  // toast div below. Success messages are cleared per-call already.
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 8000);
    return () => clearTimeout(t);
  }, [error]);

  const loadData = async () => {
    setLoading(true);
    try {
      const personUrl = showArchivedPeople ? `${API_BASE}/person?includeArchived=true` : `${API_BASE}/person`;
      const participantUrl = showArchivedParticipants ? `${API_BASE}/participants?includeArchived=true` : `${API_BASE}/participants`;
      const [peopleRes, refQualsRes, benchQualsRes, courtQualsRes, coachQualsRes, participantsRes, programTypesRes, teamsRes] = await Promise.all([
        fetch(personUrl),
        fetch(`${API_BASE}/referee-qualifications`),
        fetch(`${API_BASE}/bench-official-qualifications`),
        fetch(`${API_BASE}/court-official-qualifications`),
        fetch(`${API_BASE}/coach-qualifications`),
        fetch(participantUrl),
        fetch(`${API_BASE}/program-types`),
        fetch(`${API_BASE}/teams`)
      ]);

      if (peopleRes.ok) setPeople(await peopleRes.json());
      if (refQualsRes.ok) setRefereeQualifications(await refQualsRes.json());
      if (benchQualsRes.ok) setBenchQualifications(await benchQualsRes.json());
      if (courtQualsRes.ok) setCourtQualifications(await courtQualsRes.json());
      if (coachQualsRes.ok) setCoachQualifications(await coachQualsRes.json());
      if (participantsRes.ok) setParticipants(await participantsRes.json());
      if (programTypesRes.ok) setProgramTypes(await programTypesRes.json());
      if (teamsRes.ok) setAllTeams(await teamsRes.json());
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== PERSON ====================

  const handleAddPerson = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setSuccess('');
    if (!personLocked && (!String(personForm.name || '').trim() || !String(personForm.email || '').trim() || !String(personForm.phoneNumber || '').trim())) {
      setPersonTab('person');
      setError('Name, email and phone number are required (Person tab).');
      return;
    }

    try {
      const url = editingPersonId ? `${API_BASE}/person/${editingPersonId}` : `${API_BASE}/person`;
      const method = editingPersonId ? 'PUT' : 'POST';

      const response = await auditFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: personForm.name,
          email: personForm.email,
          phoneNumber: personForm.phoneNumber,
          accountName: personForm.accountName,
          bsbNumber: personForm.bsbNumber,
          accountNumber: personForm.accountNumber,
          gender: personForm.gender || null,
          ...(!editingPersonId && personForm.bcUserId ? { bcUserId: personForm.bcUserId } : {}),
          isActive: true,
          isActiveReferee: !!personForm.isActiveReferee,
          isActiveBench:   !!personForm.isActiveBench,
          isActiveCoach:   !!personForm.isActiveCoach,
          isActiveCourt:   !!personForm.isActiveCourt,
        })
      });

      if (response.ok) {
        setSuccess(editingPersonId ? 'Person updated successfully!' : 'Person added successfully!');
        setPersonForm({ name: '', email: '', phoneNumber: '', accountName: '', bsbNumber: '', accountNumber: '', gender: '', isActiveReferee: false, isActiveBench: false, isActiveCoach: false, isActiveCourt: false });
        setEditingPersonId(null);
        setShowPersonForm(false);
        setAddStep('form');
        loadData();
      } else {
        let serverError = '';
        try {
          const data = await response.json();
          if (data && (data.error || data.message)) serverError = data.error || data.message;
        } catch { /* not JSON */ }
        const verb = editingPersonId ? 'update' : 'add';
        setError(`Failed to ${verb} person (HTTP ${response.status})${serverError ? ': ' + serverError : ''}`);
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const loadPersonQualifications = async (personId) => {
    try {
      const response = await fetch(`${API_BASE}/person/${personId}/qualifications`);
      if (response.ok) {
        const data = await response.json();
        setSelectedPersonQualifications(data);
      }
    } catch (err) {
      console.error('Error loading person qualifications:', err);
    }
  };

  const handleAddQualificationToPerson = async (personId, category, qualificationId) => {
    if (!qualificationId) {
      setError('Please select a qualification');
      return;
    }

    try {
      const response = await auditFetch(`${API_BASE}/person/${personId}/qualifications/${category}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qualificationId: parseInt(qualificationId) })
      });

      if (response.ok) {
        setSuccess('Qualification added!');
        // The matching Active box is ticked for them (untick it if they should not be active); Update saves it.
        const activeFlagByCategory = { referee: 'isActiveReferee', benchOfficial: 'isActiveBench', coach: 'isActiveCoach', courtOfficial: 'isActiveCourt' };
        const activeFlag = activeFlagByCategory[category];
        if (activeFlag) setPersonForm(prev => ({ ...prev, [activeFlag]: true }));
        loadPersonQualifications(personId);
        setQualToAdd({ category: 'referee', qualificationId: '' });
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to add qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleRemoveQualificationFromPerson = async (personId, category, qualificationId) => {
    try {
      const response = await auditFetch(`${API_BASE}/person/${personId}/qualifications/${category}/${qualificationId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Qualification removed!');
        // Reload the person's quals so the Active checkbox can re-evaluate
        // its disabled state. If this was the LAST qual for the role,
        // also auto-untick the corresponding Active flag in the form so
        // a subsequent Save doesn't trip the backend's "active without
        // a qual" guard. The category here is the same key used in
        // selectedPersonQualifications (referee / benchOfficial / coach
        // / courtOfficial); map it to the matching isActive<Role> field.
        const remaining = (selectedPersonQualifications[category] || []).filter(q => q.id !== qualificationId);
        if (remaining.length === 0) {
          const flagByCategory = {
            referee:        'isActiveReferee',
            benchOfficial:  'isActiveBench',
            coach:          'isActiveCoach',
            courtOfficial:  'isActiveCourt',
          };
          const flag = flagByCategory[category];
          if (flag && personForm[flag]) {
            setPersonForm(prev => ({ ...prev, [flag]: false }));
          }
        }
        loadPersonQualifications(personId);
      } else {
        setError('Failed to remove qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // Team Assignments / Team Exclusions for the person being edited (People > Edit Person > Availability).
  const personTeamApi = React.useMemo(() => ({
    load: async () => {
      const [a, x] = await Promise.all([
        fetch(`${API_BASE}/person/${editingPersonId}/teams`),
        fetch(`${API_BASE}/person/${editingPersonId}/team-exclusions`)
      ]);
      return { assignments: a.ok ? await a.json() : [], exclusions: x.ok ? await x.json() : [] };
    },
    add: async (kind, teamName, division) => {
      const path = kind === 'exclusion' ? 'team-exclusions' : 'teams';
      const response = await auditFetch(`${API_BASE}/person/${editingPersonId}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamName, division })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || (kind === 'exclusion' ? 'Failed to add team exclusion' : 'Failed to assign team'));
      }
    },
    remove: async (kind, id) => {
      const path = kind === 'exclusion' ? 'team-exclusions' : 'teams';
      const response = await auditFetch(`${API_BASE}/person/${editingPersonId}/${path}/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to remove');
    }
  }), [editingPersonId]);

  // Add Person > Select from BasketballConnect: search the BC members that are not yet People.
  const searchBcMembers = async (text) => {
    setBcQuery(text);
    if (text.trim().length < 2) { setBcResults([]); return; }
    setBcSearching(true);
    try {
      const res = await auditFetch(`${API_BASE}/person/bc-search?q=${encodeURIComponent(text.trim())}`);
      setBcResults(res.ok ? await res.json() : []);
    } catch {
      setBcResults([]);
    } finally {
      setBcSearching(false);
    }
  };

  const pickBcMember = (m) => {
    setPersonForm({
      name: m.name, email: m.email, phoneNumber: m.phoneNumber, bcUserId: m.bcUserId, gender: m.gender || '',
      accountName: '', bsbNumber: '', accountNumber: '',
      isActiveReferee: false, isActiveBench: false, isActiveCoach: false, isActiveCourt: false
    });
    setAddStep('form');
  };

  // Time off for the person being edited (People > Edit Person > Availability).
  const personTimeOffApi = React.useMemo(() => ({
    load: async () => {
      const res = await fetch(`${API_BASE}/person/${editingPersonId}/time-off`);
      return res.ok ? res.json() : [];
    },
    add: async (body) => {
      const res = await auditFetch(`${API_BASE}/person/${editingPersonId}/time-off`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.needsConfirmation) return data;
      if (!res.ok) throw new Error(data.error || 'Failed to add time off');
      return data;
    },
    remove: async (id) => {
      const res = await auditFetch(`${API_BASE}/person/${editingPersonId}/time-off/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove time off');
    }
  }), [editingPersonId]);

  const handleEditPerson = (person) => {
    setPersonForm({
      name: person.name,
      email: person.email,
      phoneNumber: person.phoneNumber,
      accountName: person.accountName || '',
      bsbNumber: person.bsbNumber || '',
      accountNumber: person.accountNumber || '',
      gender: person.gender || '',
      bcUserId: person.bcUserId || '',
      // Per-role active flags. Backend exposes them via SELECT p.* on
      // /api/person/:id; coerce 1/0 (SQL Bit) to boolean.
      isActiveReferee: !!person.isActiveReferee,
      isActiveBench:   !!person.isActiveBench,
      isActiveCoach:   !!person.isActiveCoach,
      isActiveCourt:   !!person.isActiveCourt,
    });
    setEditingPersonId(person.id);
    setPersonTab('person');
    loadPersonQualifications(person.id);
    loadPersonAvailability(person.id);
    setShowPersonForm(true);
  };

  // ==================== ACTIVE PER-ROLE SUB-SECTIONS ====================

  const loadActivePeople = async (apiKey) => {
    try {
      const res = await fetch(`${API_BASE}/person/active/${apiKey}`);
      if (res.ok) {
        const data = await res.json();
        setActivePeopleByRole(prev => ({ ...prev, [apiKey]: data }));
      }
    } catch (e) { console.error(`Failed to load active ${apiKey}s:`, e); }
  };

  // Lazy-load each sub-section's active list when the tab is opened.
  useEffect(() => {
    const tabConfig = ACTIVE_ROLE_TABS.find(t => t.tabKey === activeTab);
    if (tabConfig) loadActivePeople(tabConfig.apiKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openAddActiveModal = async (apiKey, singular, plural) => {
    setActiveAddSelected(new Set());
    setActiveAddModal({ apiKey, singular, plural });
    try {
      const res = await fetch(`${API_BASE}/person/candidates/${apiKey}`);
      if (res.ok) {
        const data = await res.json();
        setActiveCandidates(data);
      } else {
        setActiveCandidates([]);
      }
    } catch (e) {
      console.error(`Failed to load ${apiKey} candidates:`, e);
      setActiveCandidates([]);
    }
  };

  const closeAddActiveModal = () => {
    setActiveAddModal(null);
    setActiveCandidates([]);
    setActiveAddSelected(new Set());
  };

  const toggleActiveCandidate = (id) => {
    setActiveAddSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submitAddActive = async () => {
    if (!activeAddModal) return;
    const ids = Array.from(activeAddSelected);
    if (ids.length === 0) { setError('Pick at least one person.'); return; }
    setActiveAddSaving(true);
    try {
      const res = await auditFetch(`${API_BASE}/person/active/${activeAddModal.apiKey}/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personIds: ids }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to mark people active.');
      } else {
        setSuccess(`Marked ${data.updated || ids.length} ${activeAddModal.plural.toLowerCase()} active.`);
        setTimeout(() => setSuccess(''), 3000);
        await loadActivePeople(activeAddModal.apiKey);
        closeAddActiveModal();
      }
    } catch (e) {
      setError('Error: ' + e.message);
    } finally {
      setActiveAddSaving(false);
    }
  };

  const removeActivePerson = async (apiKey, personId, name) => {
    if (!window.confirm(`Mark ${name} as INACTIVE for this role? They'll stop appearing in the dropdowns and AI Allocate pool. Their existing assignments aren't touched.`)) return;
    try {
      const res = await auditFetch(`${API_BASE}/person/active/${apiKey}/${personId}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to mark person inactive.');
      } else {
        setSuccess(`${name} marked inactive for this role.`);
        setTimeout(() => setSuccess(''), 3000);
        await loadActivePeople(apiKey);
      }
    } catch (e) {
      setError('Error: ' + e.message);
    }
  };

  const loadPersonAvailability = async (personId) => {
    setAvailabilityLoading(true);
    try {
      const res = await auditFetch(`${API_BASE}/person/${personId}/availability`);
      if (res.ok) {
        const data = await res.json();
        const avail = data.availability || data;
        if (Array.isArray(avail) && avail.length > 0) {
          setPersonAvailability(avail);
        } else {
          setPersonAvailability(DEFAULT_AVAILABILITY);
        }
      } else {
        setPersonAvailability(DEFAULT_AVAILABILITY);
      }
    } catch {
      setPersonAvailability(DEFAULT_AVAILABILITY);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const savePersonAvailability = async (personId) => {
    setAvailabilitySaving(true);
    try {
      const res = await auditFetch(`${API_BASE}/person/${personId}/availability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ availability: personAvailability })
      });
      if (res.ok) {
        setSuccess('Availability saved!');
        handleCancelEdit();
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to save availability');
      }
    } catch (err) {
      setError('Error saving availability: ' + err.message);
    } finally {
      setAvailabilitySaving(false);
    }
  };

  const updateAvailabilityDay = (dayOfWeek, field, value) => {
    setPersonAvailability(prev => prev.map(d =>
      d.dayOfWeek === dayOfWeek ? { ...d, [field]: value } : d
    ));
  };

  // Runs the People ↔ Basketball Connect match now (it also runs on its own
  // after every BC sync).
  const [matchingBC, setMatchingBC] = useState(false);
  const handleMatchBC = async () => {
    setMatchingBC(true);
    setError('');
    setSuccess('');
    try {
      const res = await auditFetch(`${API_BASE}/person/match-bc`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccess(`BasketballConnect match: ${data.linked} newly matched, ${data.updated} updated. ${data.totalMatched} people are matched in total.`);
      loadData();
    } catch (err) {
      setError('Match to BasketballConnect failed: ' + err.message);
    } finally {
      setMatchingBC(false);
    }
  };

  // People Management > BC Teams: put linked people on the Team Assignments of the teams they are registered in.
  const [syncingTeams, setSyncingTeams] = useState(false);
  const handleSyncBcTeams = async () => {
    setSyncingTeams(true);
    setError('');
    setSuccess('');
    try {
      const res = await auditFetch(`${API_BASE}/person/sync-team-assignments`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccess(`BasketballConnect teams: ${data.added} team assignment(s) added, ${data.removed} no longer in BasketballConnect removed${data.skippedOptOut ? `, ${data.skippedOptOut} left off because they were removed by hand` : ''}.`);
    } catch (err) {
      setError('BasketballConnect team update failed: ' + err.message);
    } finally {
      setSyncingTeams(false);
    }
  };

  const handleArchivePerson = async (id) => {
    if (!window.confirm('Archive this person? They will be hidden from all functions.')) return;

    try {
      const response = await auditFetch(`${API_BASE}/person/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Person archived!');
        loadData();
      } else {
        setError('Failed to archive person');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleCancelEdit = () => {
    setAddStep('form');
    setEditingPersonId(null);
    setPersonForm({ name: '', email: '', phoneNumber: '', accountName: '', bsbNumber: '', accountNumber: '', gender: '', isActiveReferee: false, isActiveBench: false, isActiveCoach: false, isActiveCourt: false });
    setShowPersonForm(false);
  };

  // ==================== PARTICIPANTS ====================

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const url = editingParticipantId ? `${API_BASE}/participants/${editingParticipantId}` : `${API_BASE}/participants`;
      const method = editingParticipantId ? 'PUT' : 'POST';
      const response = await auditFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(participantForm)
      });
      if (response.ok) {
        setSuccess(editingParticipantId ? 'Participant updated!' : 'Participant added!');
        setParticipantForm({ firstName: '', lastName: '', ageGroup: '', repPlayer: false, mobile: '', email: '', contactPersonName: '', notes: '', programLinks: [], externalMemberId: '', gender: '', dateOfBirth: '', repExperience: '', repLevel: '', heightCm: '', positions: [] });
        setEditingParticipantId(null);
        setShowParticipantForm(false);
        loadData();
      } else {
        setError('Failed to save participant');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleEditParticipant = (p) => {
    setParticipantForm({
      firstName: p.firstName,
      lastName: p.lastName,
      ageGroup: p.ageGroup || '',
      repPlayer: p.repPlayer || false,
      mobile: p.mobile || '',
      email: p.email || '',
      contactPersonName: p.contactPersonName || '',
      notes: p.notes || '',
      // Prefer the new programLinks shape; fall back to legacy programTypeIds for older payloads.
      programLinks: Array.isArray(p.programLinks) && p.programLinks.length > 0
        ? p.programLinks.map(l => ({ programTypeId: l.programTypeId, isFinancial: l.isFinancial }))
        : (p.programTypeIds || []).map(id => ({ programTypeId: id, isFinancial: null })),
      externalMemberId: p.externalMemberId || '',
      gender: p.gender || '',
      // Swish IQ details
      dateOfBirth: p.dateOfBirth ? String(p.dateOfBirth).slice(0, 10) : '',
      repExperience: p.repExperience || '',
      repLevel: p.repLevel || '',
      heightCm: p.heightCm ?? '',
      positions: p.swishPositions ? String(p.swishPositions).split(',').map((x) => x.trim()).filter(Boolean) : []
    });
    setEditingParticipantId(p.id);
    setShowParticipantForm(true);
  };

  const handleCancelEditParticipant = () => {
    setEditingParticipantId(null);
    setParticipantForm({ firstName: '', lastName: '', ageGroup: '', repPlayer: false, mobile: '', email: '', contactPersonName: '', notes: '', programLinks: [], externalMemberId: '', gender: '', dateOfBirth: '', repExperience: '', repLevel: '', heightCm: '', positions: [] });
    setShowParticipantForm(false);
  };

  // (handleImportParticipantsCsv removed — CSV import is deprecated.)

  const toggleParticipantProgramType = (ptId) => {
    setParticipantForm(prev => {
      const exists = prev.programLinks.some(l => l.programTypeId === ptId);
      if (exists) {
        return { ...prev, programLinks: prev.programLinks.filter(l => l.programTypeId !== ptId) };
      }
      // Default newly-ticked enrolments to Not Paid (false) so manually-
      // added participants render with the red Not Financial badge until
      // an admin explicitly marks them as Paid. Was previously null
      // (Unknown / grey).
      return { ...prev, programLinks: [...prev.programLinks, { programTypeId: ptId, isFinancial: false }] };
    });
  };

  const setParticipantProgramFinancial = (ptId, isFinancial) => {
    setParticipantForm(prev => ({
      ...prev,
      programLinks: prev.programLinks.map(l =>
        l.programTypeId === ptId ? { ...l, isFinancial } : l
      )
    }));
  };

  // ==================== AVAILABILITY LINK ====================

  const handleCopyAvailabilityLink = (person) => {
    if (!person.availabilityToken) return;
    const link = `${window.location.origin}#availability=${person.availabilityToken}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedPersonId(person.id);
      setTimeout(() => setCopiedPersonId(null), 2000);
    }).catch(() => {
      setError('Failed to copy link to clipboard');
    });
  };

  const handleEmailAvailabilityLink = async (person) => {
    if (!person.email || !person.availabilityToken) return;
    setEmailingPersonId(person.id);
    try {
      const res = await auditFetch(`${API_BASE}/person/${person.id}/email-availability-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        setEmailedPersonId(person.id);
        setTimeout(() => setEmailedPersonId(null), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to send email');
      }
    } catch (err) {
      setError('Error sending email: ' + err.message);
    } finally {
      setEmailingPersonId(null);
    }
  };

  const readOnly = !canEdit('admin', 'person');
  // Matched to Basketball Connect: name, email and phone are updated there.
  const personLocked = !!String(personForm.bcUserId || '').trim();
  const participantReadOnly = !canEdit('admin', 'participants');
  const syncedFromBC = !!(editingParticipantId && String(participantForm.externalMemberId || '').trim());

  return (
    <div className="people-page">
      <div className="people-tabs">
        {canView('admin', 'person') && (
          <button className={activeTab === 'people-management' ? 'active' : ''} onClick={() => setActiveTab('people-management')}>People Management</button>
        )}
        {/* Per-role Active rosters. One button per sub-section, gated by
            its own permission key so admins can grant access to (say) just
            Active Coaches without exposing the others. */}
        {ACTIVE_ROLE_TABS.map(t => (
          canView(TAB_PERMISSION_MAP[t.tabKey].pageKey, TAB_PERMISSION_MAP[t.tabKey].sectionKey) && (
            <button
              key={t.tabKey}
              className={activeTab === t.tabKey ? 'active' : ''}
              onClick={() => setActiveTab(t.tabKey)}
            >
              {t.plural}
            </button>
          )
        ))}
        {canView('admin', 'participants') && (
          <button className={activeTab === 'participants' ? 'active' : ''} onClick={() => setActiveTab('participants')}>Participants (Players)</button>
        )}
        {canView('admin', 'availability') && (
          <button className={activeTab === 'availability' ? 'active' : ''} onClick={() => setActiveTab('availability')}>Availability</button>
        )}
      </div>

      {/* Click-to-dismiss + auto-dismiss (8s) toasts. The styling is
          .error-message / .success-message in App.css (position:fixed). */}
      {error && (
        <div
          className="error-message"
          onClick={() => setError('')}
          title="Click to dismiss"
          style={{ cursor: 'pointer' }}
        >
          {error} <span style={{ opacity: 0.7, marginLeft: 8 }}>✕</span>
        </div>
      )}
      {success && (
        <div
          className="success-message"
          onClick={() => setSuccess('')}
          title="Click to dismiss"
          style={{ cursor: 'pointer' }}
        >
          {success} <span style={{ opacity: 0.7, marginLeft: 8 }}>✕</span>
        </div>
      )}

      {/* PEOPLE MANAGEMENT TAB */}
      {activeTab === 'people-management' && (
        <div className="admin-section">
          {loading && <div className="loading">Loading...</div>}

          {/* Modal overlay for person form */}
          {showPersonForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowPersonForm(false); handleCancelEdit(); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingPersonId ? 'Edit Person' : 'Add New Person'}</h3>
                  <button onClick={() => { setShowPersonForm(false); handleCancelEdit(); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                {!editingPersonId && addStep === 'choose' && (
                  <div style={{ display: 'grid', gap: '0.75rem', margin: '1rem 0' }}>
                    <p style={{ margin: 0, color: '#555' }}>How do you want to add this person?</p>
                    <button type="button" className="btn-submit" style={{ padding: '1rem', textAlign: 'left' }} onClick={() => setAddStep('bc')}>
                      Select from BasketballConnect
                      <span style={{ display: 'block', fontWeight: 400, fontSize: '0.85rem', opacity: 0.9 }}>Pick a member already registered in BasketballConnect. Their name, email and mobile come across.</span>
                    </button>
                    <button type="button" className="btn-submit" style={{ padding: '1rem', textAlign: 'left', background: 'white', color: '#A4161A' }} onClick={() => setAddStep('form')}>
                      Add manually
                      <span style={{ display: 'block', fontWeight: 400, fontSize: '0.85rem', opacity: 0.9 }}>Type in their details yourself.</span>
                    </button>
                  </div>
                )}

                {!editingPersonId && addStep === 'bc' && (
                  <div style={{ margin: '1rem 0' }}>
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search BasketballConnect by name, email or mobile..."
                      value={bcQuery}
                      onChange={(e) => searchBcMembers(e.target.value)}
                      style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                    />
                    <div style={{ marginTop: '0.5rem', maxHeight: '45vh', overflowY: 'auto' }}>
                      {bcSearching && <p style={{ color: '#666' }}>Searching...</p>}
                      {!bcSearching && bcQuery.trim().length >= 2 && bcResults.length === 0 && (
                        <p style={{ color: '#666' }}>No BasketballConnect members found who are not already in People.</p>
                      )}
                      {bcResults.map((m) => (
                        <button key={m.bcUserId} type="button" onClick={() => pickBcMember(m)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', background: 'white', border: '1px solid #eee', borderRadius: '6px', padding: '0.6rem 0.75rem', marginBottom: '0.4rem', cursor: 'pointer' }}>
                          <strong>{m.name}</strong>
                          <span style={{ display: 'block', color: '#666', fontSize: '0.85rem' }}>{[m.email, m.phoneNumber].filter(Boolean).join(' · ') || 'No contact details'}</span>
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                      <button type="button" className="btn-cancel" onClick={() => setAddStep('choose')}>Back</button>
                      <button type="button" className="btn-cancel" onClick={() => setAddStep('form')}>Add manually instead</button>
                    </div>
                  </div>
                )}

                {(editingPersonId || addStep === 'form') && (
                <>
                <div className="person-tabs">
                  {[
                    { key: 'person', label: 'Person' },
                    { key: 'roles', label: 'Roles', needsId: true },
                    { key: 'availability', label: 'Availability', needsId: true },
                    { key: 'bank', label: 'Bank Details' },
                  ].filter(tb => editingPersonId || !tb.needsId).map(tb => (
                    <button key={tb.key} type="button" className={personTab === tb.key ? 'active' : ''} onClick={() => setPersonTab(tb.key)}>{tb.label}</button>
                  ))}
                </div>

                {personTab === 'person' && (
                <div className="admin-form">
                  {personLocked && (
                    <>
                      <div className="form-group">
                        <label>BasketballConnect ID:</label>
                        <input type="text" value={personForm.bcUserId} readOnly disabled style={lockedInput} />
                      </div>
                      <p className="person-locked-note">
                        Matched to Basketball Connect. Name, email and phone number are updated in Basketball Connect and flow through here.
                      </p>
                    </>
                  )}
                  <div className="form-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      placeholder="John Smith"
                      value={personForm.name}
                      onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })}
                      required={!personLocked}
                      disabled={readOnly || personLocked}
                      style={personLocked ? lockedInput : undefined}
                    />
                  </div>

                  <div className="form-group">
                    <label>Email:</label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      value={personForm.email}
                      onChange={(e) => setPersonForm({ ...personForm, email: e.target.value })}
                      required={!personLocked}
                      disabled={readOnly || personLocked}
                      style={personLocked ? lockedInput : undefined}
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number:</label>
                    <input
                      type="tel"
                      placeholder="0400 000 000"
                      value={personForm.phoneNumber}
                      onChange={(e) => setPersonForm({ ...personForm, phoneNumber: e.target.value })}
                      required={!personLocked}
                      disabled={readOnly || personLocked}
                      style={personLocked ? lockedInput : undefined}
                    />
                  </div>

                  <div className="form-group">
                    <label>Gender:</label>
                    <select
                      value={personForm.gender || ''}
                      onChange={(e) => setPersonForm({ ...personForm, gender: e.target.value })}
                      disabled={readOnly}
                    >
                      <option value="">— not set —</option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Other">Other</option>
                    </select>
                    <small style={{ color: '#666', fontSize: '0.75rem' }}>
                      Optional. Used only as the lowest-weight AI Allocate factor (a female referee is gently preferred for girls/women's games). Leave unset to ignore.
                    </small>
                  </div>

                  {!readOnly && (
                    <div className="person-tab-footer">
                      <button type="button" className="btn-submit" onClick={handleAddPerson}>{editingPersonId ? 'Update' : 'Add Person'}</button>
                      <button type="button" className="btn-cancel" onClick={handleCancelEdit}>Cancel</button>
                    </div>
                  )}
                </div>
                )}

                {personTab === 'bank' && (
                <div className="admin-form">

                  <div className="form-group">
                    <label>Account Name:</label>
                    <input
                      type="text"
                      placeholder="John Smith"
                      value={personForm.accountName}
                      onChange={(e) => setPersonForm({ ...personForm, accountName: e.target.value })}
                      disabled={readOnly}
                    />
                  </div>

                  <div className="form-group">
                    <label>BSB Number:</label>
                    <input
                      type="text"
                      placeholder="000000"
                      value={personForm.bsbNumber}
                      onFocus={(e) => {
                        if (e.target.value.includes('*')) {
                          setPersonForm({ ...personForm, bsbNumber: '' });
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                        setPersonForm({ ...personForm, bsbNumber: val });
                      }}
                      maxLength={6}
                      inputMode="numeric"
                      pattern="[0-9*]{0,6}"
                      title="BSB must be exactly 6 digits"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="form-group">
                    <label>Account Number:</label>
                    <input
                      type="text"
                      placeholder="000000000"
                      value={personForm.accountNumber}
                      onFocus={(e) => {
                        if (e.target.value.includes('*')) {
                          setPersonForm({ ...personForm, accountNumber: '' });
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                        setPersonForm({ ...personForm, accountNumber: val });
                      }}
                      maxLength={9}
                      inputMode="numeric"
                      pattern="[0-9*]{0,9}"
                      title="Account number must be up to 9 digits"
                      disabled={readOnly}
                    />
                  </div>

                  {!readOnly && (
                    <div className="person-tab-footer">
                      <button type="button" className="btn-submit" onClick={handleAddPerson}>{editingPersonId ? 'Update' : 'Add Person'}</button>
                      <button type="button" className="btn-cancel" onClick={handleCancelEdit}>Cancel</button>
                    </div>
                  )}
                </div>
                )}

                {personTab === 'roles' && editingPersonId && (
                <div className="admin-form">
                  {/* Active status by role. Each ticked flag puts the person
                      into the corresponding dropdown pool (Game Planner ref/
                      bench dropdowns, Coach Planner coach dropdown, Court
                      Planner court-official dropdown) and into the AI Allocate
                      candidate pool. Untick to take them out without losing
                      their assignments / qualifications. The four People sub-
                      sections (Referees / Bench / Coach / Court) are the bulk-
                      management view of the same flags.

                      A checkbox is disabled when the person doesn't hold any
                      qualification for that role — being "active" without a
                      qualification is meaningless because the dropdowns
                      INNER JOIN on the qual table and would never include
                      the person anyway. The backend enforces the same rule
                      on PUT to defend against direct API hits. */}
                  <div className="form-section" style={{ borderTop: '1px solid #eee', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>Active Status</h4>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.8rem' }}>
                      Tick each role this person is currently active for. Inactive roles still keep historical assignments + qualifications, but the person stops appearing in the corresponding dropdowns and AI Allocate pool. A role is greyed out until you give them at least one qualification of that type below.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                      {[
                        { field: 'isActiveReferee', label: 'Active Referee',         qualKey: 'referee' },
                        { field: 'isActiveBench',   label: 'Active Bench Official',  qualKey: 'benchOfficial' },
                        { field: 'isActiveCoach',   label: 'Active Coach',           qualKey: 'coach' },
                        { field: 'isActiveCourt',   label: 'Active Court Official',  qualKey: 'courtOfficial' },
                      ].map(opt => {
                        const hasQual = (selectedPersonQualifications[opt.qualKey] || []).length > 0;
                        const blocked = !hasQual;
                        const blockedReason = `${opt.label.replace('Active ', '')}: assign at least one ${opt.label.replace('Active ', '').toLowerCase()} qualification below before marking active.`;
                        return (
                          <label
                            key={opt.field}
                            title={blocked ? blockedReason : ''}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              opacity: blocked ? 0.5 : 1,
                              cursor: blocked ? 'not-allowed' : 'default',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!personForm[opt.field]}
                              onChange={(e) => setPersonForm({ ...personForm, [opt.field]: e.target.checked })}
                              disabled={readOnly || blocked}
                            />
                            {opt.label}
                            {blocked && <span style={{ fontSize: '0.7rem', color: '#999' }}>(no qualification)</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: '1rem' }}>

                    {/* Referee Qualifications */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4>Referee Qualifications</h4>
                      <div style={{ marginBottom: '1rem' }}>
                        {selectedPersonQualifications.referee.length === 0 ? (
                          <p style={{ color: '#666' }}>No referee qualifications assigned</p>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {selectedPersonQualifications.referee.map(qual => (
                              <div key={qual.id} style={{
                                backgroundColor: '#e3f2fd',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>{qual.level}</span>
                                {!readOnly && (
                                  <button
                                    onClick={() => handleRemoveQualificationFromPerson(editingPersonId, 'referee', qual.id)}
                                    style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select
                            value={qualToAdd.category === 'referee' ? qualToAdd.qualificationId : ''}
                            onChange={(e) => setQualToAdd({ category: 'referee', qualificationId: e.target.value })}
                            style={{ flex: 1 }}
                          >
                            <option value="">Select Referee Qualification to Add</option>
                            {refereeQualifications.map(qual => (
                              <option key={qual.id} value={qual.id}>{qual.level}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAddQualificationToPerson(editingPersonId, 'referee', qualToAdd.qualificationId)}
                            className="btn-submit"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Bench Official Qualifications */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4>Bench Official Qualifications</h4>
                      <div style={{ marginBottom: '1rem' }}>
                        {selectedPersonQualifications.benchOfficial.length === 0 ? (
                          <p style={{ color: '#666' }}>No bench official qualifications assigned</p>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {selectedPersonQualifications.benchOfficial.map(qual => (
                              <div key={qual.id} style={{
                                backgroundColor: '#fff3e0',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>{qual.level}</span>
                                {!readOnly && (
                                  <button
                                    onClick={() => handleRemoveQualificationFromPerson(editingPersonId, 'benchOfficial', qual.id)}
                                    style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select
                            value={qualToAdd.category === 'benchOfficial' ? qualToAdd.qualificationId : ''}
                            onChange={(e) => setQualToAdd({ category: 'benchOfficial', qualificationId: e.target.value })}
                            style={{ flex: 1 }}
                          >
                            <option value="">Select Bench Official Qualification to Add</option>
                            {benchQualifications.map(qual => (
                              <option key={qual.id} value={qual.id}>{qual.level}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAddQualificationToPerson(editingPersonId, 'benchOfficial', qualToAdd.qualificationId)}
                            className="btn-submit"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Court Official Qualifications */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4>Court Official Qualifications</h4>
                      <div style={{ marginBottom: '1rem' }}>
                        {selectedPersonQualifications.courtOfficial.length === 0 ? (
                          <p style={{ color: '#666' }}>No court official qualifications assigned</p>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {selectedPersonQualifications.courtOfficial.map(qual => (
                              <div key={qual.id} style={{
                                backgroundColor: '#f3e5f5',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>{qual.level}</span>
                                {!readOnly && (
                                  <button
                                    onClick={() => handleRemoveQualificationFromPerson(editingPersonId, 'courtOfficial', qual.id)}
                                    style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select
                            value={qualToAdd.category === 'courtOfficial' ? qualToAdd.qualificationId : ''}
                            onChange={(e) => setQualToAdd({ category: 'courtOfficial', qualificationId: e.target.value })}
                            style={{ flex: 1 }}
                          >
                            <option value="">Select Court Official Qualification to Add</option>
                            {courtQualifications.map(qual => (
                              <option key={qual.id} value={qual.id}>{qual.level}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAddQualificationToPerson(editingPersonId, 'courtOfficial', qualToAdd.qualificationId)}
                            className="btn-submit"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Coach Qualifications */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4>Coach Qualifications</h4>
                      <div style={{ marginBottom: '1rem' }}>
                        {selectedPersonQualifications.coach.length === 0 ? (
                          <p style={{ color: '#666' }}>No coach qualifications assigned</p>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {selectedPersonQualifications.coach.map(qual => (
                              <div key={qual.id} style={{
                                backgroundColor: '#e8f5e9',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>{qual.level}</span>
                                {!readOnly && (
                                  <button
                                    onClick={() => handleRemoveQualificationFromPerson(editingPersonId, 'coach', qual.id)}
                                    style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select
                            value={qualToAdd.category === 'coach' ? qualToAdd.qualificationId : ''}
                            onChange={(e) => setQualToAdd({ category: 'coach', qualificationId: e.target.value })}
                            style={{ flex: 1 }}
                          >
                            <option value="">Select Coach Qualification to Add</option>
                            {coachQualifications.map(qual => (
                              <option key={qual.id} value={qual.id}>{qual.level}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAddQualificationToPerson(editingPersonId, 'coach', qualToAdd.qualificationId)}
                            className="btn-submit"
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>


                  </div>
                  {!readOnly && (
                    <div className="person-tab-footer">
                      <button type="button" className="btn-submit" onClick={handleAddPerson}>Update</button>
                      <button type="button" className="btn-cancel" onClick={handleCancelEdit}>Cancel</button>
                    </div>
                  )}
                </div>
                )}

                {personTab === 'availability' && editingPersonId && (
                <div className="admin-form">
                    {/* Availability */}
                    <div style={{ marginTop: '1.5rem' }}>
                      <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Availability
                        
                      </h4>
                      {availabilityLoading ? (
                        <p style={{ color: '#666' }}>Loading availability...</p>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #ddd' }}>
                              <th style={{ textAlign: 'left', padding: '0.4rem' }}>Day</th>
                              <th style={{ textAlign: 'center', padding: '0.4rem' }}>Available</th>
                              <th style={{ textAlign: 'center', padding: '0.4rem' }}>Start</th>
                              <th style={{ textAlign: 'center', padding: '0.4rem' }}>End</th>
                            </tr>
                          </thead>
                          <tbody>
                            {personAvailability.sort((a, b) => {
                              const order = [1, 2, 3, 4, 5, 6, 0];
                              return order.indexOf(a.dayOfWeek) - order.indexOf(b.dayOfWeek);
                            }).map(day => (
                              <tr key={day.dayOfWeek} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.4rem', fontWeight: 500 }}>{day.dayName}</td>
                                <td style={{ textAlign: 'center', padding: '0.4rem' }}>
                                  <input
                                    type="checkbox"
                                    checked={day.isAvailable}
                                    onChange={(e) => updateAvailabilityDay(day.dayOfWeek, 'isAvailable', e.target.checked)}
                                    disabled={readOnly}
                                  />
                                </td>
                                <td style={{ textAlign: 'center', padding: '0.4rem' }}>
                                  <input
                                    type="time"
                                    value={day.startTime}
                                    onChange={(e) => updateAvailabilityDay(day.dayOfWeek, 'startTime', e.target.value)}
                                    disabled={readOnly || !day.isAvailable}
                                    style={{ opacity: day.isAvailable ? 1 : 0.4 }}
                                  />
                                </td>
                                <td style={{ textAlign: 'center', padding: '0.4rem' }}>
                                  <input
                                    type="time"
                                    value={day.endTime}
                                    onChange={(e) => updateAvailabilityDay(day.dayOfWeek, 'endTime', e.target.value)}
                                    disabled={readOnly || !day.isAvailable}
                                    style={{ opacity: day.isAvailable ? 1 : 0.4 }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Team Assignments and Team Exclusions (they save as you add / remove, not with Update). */}
                    <PersonTimeOff api={personTimeOffApi} readOnly={readOnly} />
                    <PersonTeamLists api={personTeamApi} teams={allTeams} readOnly={readOnly} />

                  {!readOnly && (
                    <div className="person-tab-footer">
                      <button type="button" className="btn-submit" onClick={() => savePersonAvailability(editingPersonId)} disabled={availabilitySaving}>{availabilitySaving ? 'Saving...' : 'Update'}</button>
                      <button type="button" className="btn-cancel" onClick={handleCancelEdit}>Cancel</button>
                    </div>
                  )}
                </div>
                )}
                </>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            {!readOnly && (
              <button className="btn-submit" style={{ background: 'white', color: '#A4161A' }} onClick={handleMatchBC} disabled={matchingBC} title="Match People to their BasketballConnect account now (first and last name plus email or mobile)">
                {matchingBC ? 'Matching…' : 'BC Match'}
              </button>
            )}
            {!readOnly && (
              <button className="btn-submit" style={{ background: 'white', color: '#A4161A' }} onClick={handleSyncBcTeams} disabled={syncingTeams} title="Put people linked to BasketballConnect on the Team Assignments of the teams they are registered in (it also runs after every sync)">
                {syncingTeams ? 'Updating…' : 'BC Teams'}
              </button>
            )}
            {!readOnly && (
              <button className="btn-submit" onClick={() => { setShowPersonForm(true); setEditingPersonId(null); setPersonTab('person'); setAddStep('choose'); setBcQuery(''); setBcResults([]); setPersonForm({ name: '', email: '', phoneNumber: '', accountName: '', bsbNumber: '', accountNumber: '', gender: '', isActiveReferee: false, isActiveBench: false, isActiveCoach: false, isActiveCourt: false }); }}>+ Add Person</button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <input
              type="text"
              placeholder="Search people..."
              value={peopleSearch}
              onChange={(e) => setPeopleSearch(e.target.value)}
              style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedPeople} onChange={(e) => setShowArchivedPeople(e.target.checked)} />
              Show Archived
            </label>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>BasketballConnect ID</th>
                <th>Roster Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {people.filter(person => {
                if (!peopleSearch) return true;
                const search = peopleSearch.toLowerCase();
                return (person.name || '').toLowerCase().includes(search) ||
                       (person.email || '').toLowerCase().includes(search) ||
                       (person.phoneNumber || '').toLowerCase().includes(search) ||
                       String(person.bcUserId || '').toLowerCase().includes(search);
              }).map(person => (
                <tr key={person.id} style={person.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td><span style={{ cursor: person.isArchived ? 'default' : 'pointer', color: person.isArchived ? '#999' : '#A4161A', textDecoration: person.isArchived ? 'none' : 'underline' }} onClick={() => !person.isArchived && handleEditPerson(person)}>{person.name}{person.isArchived ? ' (Archived)' : ''}</span></td>
                  <td>{person.email}</td>
                  <td>{person.phoneNumber}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{person.bcUserId || <span style={{ color: '#bbb' }}>–</span>}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                      {person.availabilityToken ? (
                        <>
                          <button
                            className="btn-link-action"
                            onClick={() => handleCopyAvailabilityLink(person)}
                            title="Copy roster link to clipboard"
                            style={{
                              padding: '0.3rem 0.5rem',
                              fontSize: '0.75rem',
                              background: copiedPersonId === person.id ? '#2e7d32' : '#A4161A',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {copiedPersonId === person.id ? 'Copied!' : 'Copy Link'}
                          </button>
                          <button
                            className="btn-link-action"
                            onClick={() => handleEmailAvailabilityLink(person)}
                            title={person.email ? 'Email roster link' : 'No email address'}
                            disabled={!person.email || emailingPersonId === person.id}
                            style={{
                              padding: '0.3rem 0.5rem',
                              fontSize: '0.75rem',
                              background: emailedPersonId === person.id ? '#2e7d32' : (!person.email ? '#ccc' : '#ff9800'),
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: !person.email || emailingPersonId === person.id ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {emailingPersonId === person.id ? 'Sending...' : emailedPersonId === person.id ? 'Sent!' : 'Email Link'}
                          </button>
                        </>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.75rem' }}>No token</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {!person.isArchived && <button className="btn-edit" onClick={() => handleEditPerson(person)}>Edit</button>}
                    {!readOnly && !person.isArchived && <button className="btn-delete" onClick={() => handleArchivePerson(person.id)}>Archive</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AVAILABILITY TAB */}
      {activeTab === 'availability' && (
        <Availability />
      )}

      {/* PARTICIPANTS TAB */}
      {activeTab === 'participants' && (
        <div className="admin-section">
          {loading && <div className="loading">Loading...</div>}

          {/* Modal overlay for participant form */}
          {showParticipantForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowParticipantForm(false); handleCancelEditParticipant(); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingParticipantId ? 'Edit Participant' : 'Add New Participant'}</h3>
                  <button onClick={() => { setShowParticipantForm(false); handleCancelEditParticipant(); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddParticipant} className="admin-form">
                  {/* External ID, name, email and mobile come from Basketball
                      Connect and are overwritten on every sync, so they're
                      read-only here. */}
                  {syncedFromBC && (
                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#555', background: '#f4f6f9', border: '1px solid #dde3ea', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                      External ID, name, email and mobile are synced from Basketball Connect. Change them there.
                    </p>
                  )}
                  <div className="form-group">
                    <label>External ID:</label>
                    <input type="text" value={participantForm.externalMemberId} readOnly disabled style={readOnlyInput} />
                  </div>
                  <div className="form-group">
                    <label>First Name:</label>
                    <input type="text" placeholder="First name" value={participantForm.firstName} onChange={(e) => setParticipantForm({ ...participantForm, firstName: e.target.value })} required disabled={participantReadOnly || syncedFromBC} style={syncedFromBC ? readOnlyInput : undefined} />
                  </div>
                  <div className="form-group">
                    <label>Last Name:</label>
                    <input type="text" placeholder="Last name" value={participantForm.lastName} onChange={(e) => setParticipantForm({ ...participantForm, lastName: e.target.value })} required disabled={participantReadOnly || syncedFromBC} style={syncedFromBC ? readOnlyInput : undefined} />
                  </div>
                  <div className="form-group">
                    <label>Gender:</label>
                    <select value={participantForm.gender} onChange={(e) => setParticipantForm({ ...participantForm, gender: e.target.value })} disabled={participantReadOnly}>
                      <option value="">— select —</option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Age Group:</label>
                    <select value={participantForm.ageGroup} onChange={(e) => setParticipantForm({ ...participantForm, ageGroup: e.target.value })} disabled={participantReadOnly}>
                      <option value="">Select Age Group</option>
                      {['U10', 'U12', 'U14', 'U16', 'U18', 'Open', 'Other'].map(ag => (
                        <option key={ag} value={ag}>{ag}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={participantForm.repPlayer} onChange={(e) => setParticipantForm({ ...participantForm, repPlayer: e.target.checked })} disabled={participantReadOnly} />
                      Rep Player
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Mobile:</label>
                    <input type="tel" placeholder="0400 000 000" value={participantForm.mobile} onChange={(e) => setParticipantForm({ ...participantForm, mobile: e.target.value })} disabled={participantReadOnly || syncedFromBC} style={syncedFromBC ? readOnlyInput : undefined} />
                  </div>
                  <div className="form-group">
                    <label>Email:</label>
                    <input type="email" placeholder="email@example.com" value={participantForm.email} onChange={(e) => setParticipantForm({ ...participantForm, email: e.target.value })} disabled={participantReadOnly || syncedFromBC} style={syncedFromBC ? readOnlyInput : undefined} />
                  </div>
                  <div className="form-group">
                    <label>Contact Person:</label>
                    <input type="text" placeholder="Parent/Guardian name" value={participantForm.contactPersonName} onChange={(e) => setParticipantForm({ ...participantForm, contactPersonName: e.target.value })} disabled={participantReadOnly} />
                  </div>
                  {/* Swish IQ details: the same data Swish IQ shows for this player. */}
                  <div className="form-group" style={{ borderTop: '1px solid #eee', paddingTop: '0.75rem' }}>
                    <label style={{ fontWeight: 700 }}>SwishIQ Trials details</label>
                  </div>
                  <div className="form-group">
                    <label>Date of birth:</label>
                    <input type="date" value={participantForm.dateOfBirth} onChange={(e) => setParticipantForm({ ...participantForm, dateOfBirth: e.target.value })} disabled={participantReadOnly} />
                  </div>
                  <div className="form-group">
                    <label>Rep experience:</label>
                    <select value={participantForm.repExperience} onChange={(e) => setParticipantForm({ ...participantForm, repExperience: e.target.value })} disabled={participantReadOnly}>
                      <option value="">—</option>
                      {['IBA', 'Other', 'None'].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Rep level:</label>
                    <select value={participantForm.repLevel} onChange={(e) => setParticipantForm({ ...participantForm, repLevel: e.target.value })} disabled={participantReadOnly}>
                      <option value="">—</option>
                      {['WSC', 'SJL', 'JPL', 'BG', 'YL', 'Other'].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Height (cm):</label>
                    <input type="number" min="50" max="260" placeholder="e.g. 175" value={participantForm.heightCm} onChange={(e) => setParticipantForm({ ...participantForm, heightCm: e.target.value })} disabled={participantReadOnly} />
                  </div>
                  <div className="form-group">
                    <label>Positions:</label>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {['Guard', 'Forward', 'Centre'].map((pos) => (
                        <label key={pos} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 400 }}>
                          <input
                            type="checkbox"
                            checked={participantForm.positions.includes(pos)}
                            onChange={(e) => setParticipantForm({
                              ...participantForm,
                              positions: e.target.checked ? [...participantForm.positions, pos] : participantForm.positions.filter((x) => x !== pos),
                            })}
                            disabled={participantReadOnly}
                          />
                          {pos}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes:</label>
                    <textarea placeholder="Any additional notes" value={participantForm.notes} onChange={(e) => setParticipantForm({ ...participantForm, notes: e.target.value })} rows={3} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} disabled={participantReadOnly} />
                  </div>

                  {programTypes.length > 0 && (
                    <div className="form-group">
                      <label>Coaching Programs:</label>
                      <ProgramMultiSelect
                        programTypes={programTypes}
                        links={participantForm.programLinks}
                        onToggle={toggleParticipantProgramType}
                        onFinancialChange={setParticipantProgramFinancial}
                        disabled={participantReadOnly}
                      />
                    </div>
                  )}

                  {editingParticipantId && <ParticipantRegistrations participantId={editingParticipantId} />}
                  {editingParticipantId && <ParticipantSwishSessions participantId={editingParticipantId} />}

                  {!participantReadOnly && (
                    <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="submit" className="btn-submit">{editingParticipantId ? 'Update Participant' : 'Add Participant'}</button>
                      {editingParticipantId && (
                        <button type="button" className="btn-cancel" onClick={handleCancelEditParticipant}>Cancel</button>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <input
              type="text"
              placeholder="Search participants..."
              value={participantSearch}
              onChange={(e) => setParticipantSearch(e.target.value)}
              style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedParticipants} onChange={(e) => setShowArchivedParticipants(e.target.checked)} />
              Show Archived
            </label>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Gender</th>
                <th>Age Group</th>
                <th>Rep</th>
                <th>DOB</th>
                <th>Positions</th>
                <th>Rep exp. / level</th>
                <th>Height</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Contact Person</th>
                <th>Programs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {participants.filter(p => {
                if (!participantSearch) return true;
                const search = participantSearch.toLowerCase();
                return (`${p.firstName} ${p.lastName}`).toLowerCase().includes(search) ||
                       (p.email || '').toLowerCase().includes(search) ||
                       (p.mobile || '').toLowerCase().includes(search) ||
                       (p.contactPersonName || '').toLowerCase().includes(search) ||
                       (p.programTypeNames || '').toLowerCase().includes(search) ||
                       (p.externalMemberId || '').toLowerCase().includes(search);
              }).map(p => (
                <tr key={p.id} style={p.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.externalMemberId || '-'}</td>
                  <td><span style={{ cursor: p.isArchived ? 'default' : 'pointer', color: p.isArchived ? '#999' : '#A4161A', textDecoration: p.isArchived ? 'none' : 'underline' }} onClick={() => !p.isArchived && handleEditParticipant(p)}>{p.firstName} {p.lastName}{p.isArchived ? ' (Archived)' : ''}</span></td>
                  <td>{p.gender || '-'}</td>
                  <td>{p.ageGroup || '-'}</td>
                  <td>{p.repPlayer ? 'Yes' : '-'}</td>
                  <td>{p.dateOfBirth ? new Date(p.dateOfBirth).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }) : '-'}</td>
                  <td>{p.swishPositions || '-'}</td>
                  <td>{[p.repExperience, p.repLevel].filter(Boolean).join(' / ') || '-'}</td>
                  <td>{p.heightCm ? `${p.heightCm} cm` : '-'}</td>
                  <td>{p.mobile || '-'}</td>
                  <td>{p.email || '-'}</td>
                  <td>{p.contactPersonName || '-'}</td>
                  <td>{p.programTypeNames || '-'}</td>
                  <td>
                    {!p.isArchived && <button className="btn-edit" onClick={() => handleEditParticipant(p)}>Edit</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PER-ROLE ACTIVE SUB-SECTIONS — Referees / Bench / Coach / Court.
          One render block, four data sources via ACTIVE_ROLE_TABS. Each
          sub-section shows the currently-active people with their
          qualifications + a remove button, plus an "Add Active …" button
          that opens a multi-select modal of qualified-but-not-yet-active
          candidates. */}
      {ACTIVE_ROLE_TABS.map(t => {
        if (activeTab !== t.tabKey) return null;
        const rows = activePeopleByRole[t.apiKey] || [];
        const sectionEditable = canEdit(TAB_PERMISSION_MAP[t.tabKey].pageKey, TAB_PERMISSION_MAP[t.tabKey].sectionKey);
        return (
          <div key={t.tabKey} className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div>
                <h2 style={{ margin: 0 }}>Active {t.plural}</h2>
                <p style={{ margin: '0.25rem 0 0', color: '#666', fontSize: '0.85rem' }}>
                  These people are eligible to be assigned as {t.singular.toLowerCase()}s on the Game / Coach / Court planners and considered by AI Allocate. The list is independent of qualifications — a person needs both a qualification AND the Active flag to appear in dropdowns.
                </p>
              </div>
              {sectionEditable && (
                <button
                  className="btn-submit"
                  onClick={() => openAddActiveModal(t.apiKey, t.singular, t.plural)}
                >
                  + Add Active {t.singular}
                </button>
              )}
            </div>

            {rows.length === 0 ? (
              <p style={{ color: '#666' }}>No active {t.plural.toLowerCase()} yet. Click <em>+ Add Active {t.singular}</em> to pick from people who hold a {t.singular.toLowerCase()} qualification.</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Qualifications</th>
                    <th>Email</th>
                    <th>Phone</th>
                    {sectionEditable && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(p => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{(p.qualifications || []).join(', ') || <em style={{ color: '#999' }}>none on file</em>}</td>
                      <td>{p.email || ''}</td>
                      <td>{p.phoneNumber || ''}</td>
                      {sectionEditable && (
                        <td>
                          <button
                            className="btn-delete"
                            onClick={() => removeActivePerson(t.apiKey, p.id, p.name)}
                            title={`Mark ${p.name} inactive for this role`}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {/* ADD ACTIVE … MODAL — opens from any of the four sub-sections.
          Lists every person with the relevant qualification who isn't
          already active. Multi-select; one POST writes them all. */}
      {activeAddModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeAddActiveModal}
        >
          <div
            style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '90%', maxWidth: 720, maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>Add Active {activeAddModal.singular}</h3>
              <button onClick={closeAddActiveModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
            </div>
            <p style={{ margin: '0 0 0.75rem 0', color: '#666', fontSize: '0.85rem' }}>
              People who hold a {activeAddModal.singular.toLowerCase()} qualification but aren't yet flagged active for the role. Tick each person you want to mark active, then click <em>Add Selected</em>.
            </p>
            {activeCandidates.length === 0 ? (
              <p style={{ color: '#666' }}>
                No candidates available. Either everyone qualified is already active, or no-one holds a {activeAddModal.singular.toLowerCase()} qualification yet (assign one via People &rarr; People Management &rarr; Edit).
              </p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>Name</th>
                    <th>Qualifications</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCandidates.map(c => (
                    <tr
                      key={c.id}
                      style={{ cursor: 'pointer', background: activeAddSelected.has(c.id) ? '#fff8e1' : undefined }}
                      onClick={() => toggleActiveCandidate(c.id)}
                    >
                      <td><input type="checkbox" checked={activeAddSelected.has(c.id)} onChange={() => toggleActiveCandidate(c.id)} /></td>
                      <td>{c.name}</td>
                      <td>{(c.qualifications || []).join(', ') || <em style={{ color: '#999' }}>none on file</em>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn-cancel" onClick={closeAddActiveModal} disabled={activeAddSaving}>Cancel</button>
              <button
                className="btn-submit"
                onClick={submitAddActive}
                disabled={activeAddSelected.size === 0 || activeAddSaving}
              >
                {activeAddSaving ? 'Saving…' : `Add Selected (${activeAddSelected.size})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const readOnlyInput = { background: '#f1f3f5', color: '#555', cursor: 'not-allowed' };

// Coaching Programs as a drop-down of tick boxes. Ticking a program adds it
// as Financial: No (not paid); the Basketball Connect sync marks it paid once
// they pay for the registration there.
function ProgramMultiSelect({ programTypes, links, onToggle, onFinancialChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const selected = links
    .map((l) => ({ link: l, pt: programTypes.find((pt) => pt.id === l.programTypeId) }))
    .filter((x) => x.pt);
  const f = filter.trim().toLowerCase();
  const options = programTypes.filter((pt) => !f || pt.name.toLowerCase().includes(f));

  return (
    <div ref={boxRef} style={{ position: 'relative', marginTop: '0.4rem' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', textAlign: 'left', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, background: disabled ? '#f1f3f5' : 'white', cursor: disabled ? 'default' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ color: selected.length ? '#222' : '#888' }}>
          {selected.length ? `${selected.length} program${selected.length === 1 ? '' : 's'} selected` : 'Select coaching programs…'}
        </span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4, background: 'white', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto' }}>
          <div style={{ padding: 6, borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: 'white' }}>
            <input
              type="text"
              placeholder="Search programs…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
            />
          </div>
          {options.length === 0 && <div style={{ padding: '8px 10px', color: '#888', fontSize: '0.85rem' }}>No programs match.</div>}
          {options.map((pt) => {
            const checked = links.some((l) => l.programTypeId === pt.id);
            return (
              <label key={pt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', cursor: 'pointer', fontSize: '0.9rem', background: checked ? '#f3f8ff' : 'white' }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(pt.id)} />
                <span>{pt.name}</span>
              </label>
            );
          })}
        </div>
      )}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.5rem' }}>
          {selected.map(({ link, pt }) => (
            <div key={pt.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem', background: '#fafafa', border: '1px solid #eee', borderRadius: 4, padding: '4px 8px' }}>
              <span style={{ flex: 1 }}>{pt.name}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#444' }}>
                <span>Financial:</span>
                <select
                  value={link.isFinancial === true ? 'yes' : link.isFinancial === false ? 'no' : 'unknown'}
                  onChange={(e) => {
                    const v = e.target.value;
                    onFinancialChange(pt.id, v === 'yes' ? true : v === 'no' ? false : null);
                  }}
                  disabled={disabled}
                  style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc' }}
                >
                  <option value="unknown">Unknown</option>
                  <option value="yes">Yes (paid)</option>
                  <option value="no">No (not paid)</option>
                </select>
              </label>
              {!disabled && (
                <button type="button" onClick={() => onToggle(pt.id)} title="Remove" style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '1rem' }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const lockedInput = { background: '#f1f3f5', color: '#555', cursor: 'not-allowed' };
